import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { DataTable } from '../ui/data-table';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../ui/modal';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import FileUpload from '../ui/FileUpload';
import { classAssignmentAPI, assignmentSubmissionAPI, userAPI, cacheUtils } from '../../services/api';

const ClassAssignment = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const classTitle = location.state?.classTitle || 'Class';
  
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isSubmissionsModalOpen, setIsSubmissionsModalOpen] = useState(false);
  const [isGradeModalOpen, setIsGradeModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingAssignmentId, setEditingAssignmentId] = useState(null);
  const [deletingAssignmentId, setDeletingAssignmentId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGrading, setIsGrading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  const [gradeFormData, setGradeFormData] = useState({
    score: '',
    feedback: '',
    graded_by:  1,
  });
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assignment_type: 'homework',
    due_date: '',
    max_score: 100,
    attachment_url: '',
    created_by : 1,
  });

  const assignmentTypes = [
    { value: 'homework', label: 'Homework' },
    { value: 'project', label: 'Project' },
    { value: 'quiz', label: 'Quiz' },
    { value: 'exam', label: 'Exam' },
    { value: 'lab', label: 'Lab' },
    { value: 'reading', label: 'Reading' },
    { value: 'other', label: 'Other' },
  ];

  const columns = [
    {
      accessorKey: 'id',
      header: 'ID',
      sortable: true
    },
    {
      accessorKey: 'title',
      header: 'Title',
      sortable: true,
      cell: (row) => (
        <div>
          <p className="font-medium text-gray-900">{row.title}</p>
          {row.assignment_type && (
            <span className="text-xs text-gray-500 capitalize">{row.assignment_type}</span>
          )}
        </div>
      )
    },
    {
      accessorKey: 'description',
      header: 'Description',
      sortable: false,
      cell: (row) => (
        <span className="text-gray-600 truncate max-w-xs block">
          {row.description || 'No description'}
        </span>
      )
    },
    {
      accessorKey: 'due_date',
      header: 'Due Date',
      sortable: true,
      cell: (row) => {
        if (!row.due_date) return 'No due date';
        const dueDate = new Date(row.due_date);
        const now = new Date();
        const isOverdue = dueDate < now;
        
        return (
          <div className={isOverdue ? 'text-red-600 font-medium' : 'text-gray-900'}>
            {dueDate.toLocaleDateString()}
            {isOverdue && <span className="text-xs block">Overdue</span>}
          </div>
        );
      }
    },
    {
      accessorKey: 'max_score',
      header: 'Max Score',
      sortable: true,
      cell: (row) => (
        <span className="font-medium text-gray-900">{row.max_score || 'N/A'}</span>
      )
    },
    {
      accessorKey: 'created_at',
      header: 'Created',
      sortable: true,
      cell: (row) => new Date(row.created_at).toLocaleDateString()
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      sortable: false,
      className: 'w-40',
      cell: (row) => (
        <div className="flex space-x-1">
          <button
            onClick={() => handleViewSubmissions(row)}
            className="p-1.5 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 rounded-full transition-colors"
            title="See user submissions"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </button>
          <button
            onClick={() => handleViewAssignment(row)}
            className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
            title="View assignment details"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          {row.attachment_url && (
            <button
              onClick={() => window.open(row.attachment_url, '_blank')}
              className="p-1.5 text-green-500 hover:text-green-700 hover:bg-green-50 rounded-full transition-colors"
              title="Download attachment"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </button>
          )}
          <button
            onClick={() => handleEditAssignment(row)}
            className="p-1.5 text-purple-500 hover:text-purple-700 hover:bg-purple-50 rounded-full transition-colors"
            title="Edit assignment"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => handleDeleteAssignment(row.id)}
            disabled={deletingAssignmentId === row.id}
            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Delete assignment"
          >
            {deletingAssignmentId === row.id ? (
              <div className="animate-spin w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            )}
          </button>
        </div>
      )
    }
  ];

  useEffect(() => {
    fetchAssignments();
    fetchCurrentUser();
  }, [classId]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const response = await classAssignmentAPI.getClassAssignmentsByClassId(classId);
      setAssignments(response.data || []);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const userStr = localStorage.getItem('adminUser') || sessionStorage.getItem('adminUser');
      if (userStr) {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
        console.log("Current userid",  user);
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const handleFileUploadSuccess = (url, fileInfo) => {
    setFormData(prev => ({
      ...prev,
      attachment_url: url
    }));
  };

  const handleFileUploadError = (error) => {
    console.error('File upload error:', error);
    alert('Failed to upload file. Please try again.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title) {
      alert('Please enter an assignment title');
      return;
    }

    try {
      setIsSubmitting(true);
      const submitData = {
        class_id: parseInt(classId),
        title: formData.title,
        description: formData.description,
        assignment_type: formData.assignment_type,
        due_date: formData.due_date || null,
        max_score: parseFloat(formData.max_score) || 100,
        attachment_url: formData.attachment_url || null,
        created_by: 1,
      };

      if (isEditing) {
        await classAssignmentAPI.updateClassAssignment(editingAssignmentId, submitData);
        cacheUtils.clearClassAssignmentCache();
      } else {
        await classAssignmentAPI.createClassAssignment(submitData);
        cacheUtils.clearClassAssignmentCache();
      }

      setIsModalOpen(false);
      resetForm();
      fetchAssignments();
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} assignment:`, error);
      alert(`Failed to ${isEditing ? 'update' : 'create'} assignment. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditAssignment = (assignment) => {
    setIsEditing(true);
    setEditingAssignmentId(assignment.id);
    setFormData({
      title: assignment.title || '',
      description: assignment.description || '',
      assignment_type: assignment.assignment_type || 'homework',
      due_date: assignment.due_date ? new Date(assignment.due_date).toISOString().split('T')[0] : '',
      max_score: assignment.max_score || 100,
      attachment_url: assignment.attachment_url || '',
    });
    setIsModalOpen(true);
  };

  const handleViewAssignment = (assignment) => {
    setSelectedAssignment(assignment);
    setIsViewModalOpen(true);
  };

  const handleViewSubmissions = async (assignment) => {
    setSelectedAssignment(assignment);
    setIsSubmissionsModalOpen(true);
    setLoadingSubmissions(true);
    
    try {
      // Fetch submissions for this assignment
      const submissionsResponse = await assignmentSubmissionAPI.getAssignmentSubmissionsByAssignment(assignment.id);
      const submissionsData = submissionsResponse.data || [];
      
      // Fetch all users to get user details
      const usersResponse = await userAPI.getAllUsers();
      const usersData = usersResponse.data || [];
      
      // Create a user map for quick lookup
      const userMap = {};
      usersData.forEach(user => {
        userMap[user.id] = user;
      });
      
      // Enrich submissions with user data
      const enrichedSubmissions = submissionsData.map(submission => {
        const user = userMap[submission.user_id] || {};
        return {
          ...submission,
          user_first_name: user.first_name || 'Unknown',
          user_last_name: user.last_name || 'User',
          user_email: user.email || 'N/A',
          user_username: user.username || 'N/A',
        };
      });
      
      setSubmissions(enrichedSubmissions);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      setSubmissions([]);
    } finally {
      setLoadingSubmissions(false);
    }
  };

  const handleGradeSubmission = (submission) => {
    setSelectedSubmission(submission);
    setGradeFormData({
      score: submission.score || '',
      feedback: submission.feedback || '',
      graded_by: 1,
    });
    setIsGradeModalOpen(true);
  };

  const handleGradeFormChange = (e) => {
    const { name, value } = e.target;
    setGradeFormData(prev => ({
      ...prev,
      [name]: name === 'score' ? parseFloat(value) || '' : value
    }));
  };

  const handleSubmitGrade = async (e) => {
    e.preventDefault();

    if (gradeFormData.score === '' || gradeFormData.score === null) {
      alert('Please enter a score');
      return;
    }

    try {
      setIsGrading(true);
      const gradeData = {
        score: parseFloat(gradeFormData.score),
        feedback: gradeFormData.feedback,
        graded_by: 1,
      };
      console.log("Current userid",  currentUser?.id);
      await assignmentSubmissionAPI.gradeAssignmentSubmission(selectedSubmission.id, gradeData);
      
      // Refresh submissions list
      await handleViewSubmissions(selectedAssignment);
      
      setIsGradeModalOpen(false);
      setSelectedSubmission(null);
      setGradeFormData({
        score: '',
        feedback: '',
        graded_by: null,
      });
    } catch (error) {
      console.error('Error grading submission:', error);
      alert('Failed to grade submission. Please try again.');
    } finally {
      setIsGrading(false);
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingAssignmentId(assignmentId);
      await classAssignmentAPI.deleteClassAssignment(assignmentId);
      cacheUtils.clearClassAssignmentCache();
      fetchAssignments();
    } catch (error) {
      console.error('Error deleting assignment:', error);
      alert('Failed to delete assignment. Please try again.');
    } finally {
      setDeletingAssignmentId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      assignment_type: 'homework',
      due_date: '',
      max_score: 100,
      attachment_url: '',
    });
    setIsEditing(false);
    setEditingAssignmentId(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleGoBack = () => {
    navigate(`/classes/${classId}`);
  };

  const getAssignmentTypeColor = (type) => {
    const colors = {
      homework: 'bg-blue-100 text-blue-800',
      project: 'bg-purple-100 text-purple-800',
      quiz: 'bg-green-100 text-green-800',
      exam: 'bg-red-100 text-red-800',
      lab: 'bg-yellow-100 text-yellow-800',
      reading: 'bg-indigo-100 text-indigo-800',
      other: 'bg-gray-100 text-gray-800',
    };
    return colors[type] || colors.other;
  };

  return (
    <div className="max-w-6xl mx-auto p-9">
      <div className="mb-8 border-b border-gray-200 pb-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleGoBack}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
              title="Back to class details"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Class Assignments</h1>
              <p className="text-lg text-gray-600">{classTitle} - Manage assignments</p>
            </div>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Create Assignment</span>
          </button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={assignments}
        searchKey="title"
        searchPlaceholder="Search assignments..."
        loading={loading}
        emptyMessage="No assignments found."
      />

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} disableBackdropClose={true}>
        <form onSubmit={handleSubmit}>
          <ModalHeader>
            <h2 className="text-xl font-semibold">
              {isEditing ? 'Edit Assignment' : 'Create New Assignment'}
            </h2>
          </ModalHeader>

          <ModalBody className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <Input
                id="title"
                name="title"
                type="text"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="Enter assignment title"
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Enter assignment description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="assignment_type" className="block text-sm font-medium text-gray-700 mb-1">
                  Assignment Type
                </label>
                <select
                  id="assignment_type"
                  name="assignment_type"
                  value={formData.assignment_type}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  {assignmentTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="max_score" className="block text-sm font-medium text-gray-700 mb-1">
                  Max Score
                </label>
                <Input
                  id="max_score"
                  name="max_score"
                  type="number"
                  value={formData.max_score}
                  onChange={handleInputChange}
                  min="0"
                  step="0.5"
                  placeholder="100"
                />
              </div>
            </div>

            <div>
              <label htmlFor="due_date" className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
              </label>
              <Input
                id="due_date"
                name="due_date"
                type="date"
                value={formData.due_date}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Attachment (Optional)
              </label>
              <FileUpload
                onUploadSuccess={handleFileUploadSuccess}
                onUploadError={handleFileUploadError}
                accept="*/*"
                maxSize={50 * 1024 * 1024} // 50MB
                folder="class-assignments"
                showPreview={false}
                currentUrl={formData.attachment_url}
              />
              {formData.attachment_url && (
                <div className="mt-2">
                  <p className="text-xs text-green-600">File uploaded successfully</p>
                  <a 
                    href={formData.attachment_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline"
                  >
                    View uploaded file
                  </a>
                </div>
              )}
            </div>
          </ModalBody>

          <ModalFooter>
            <Button
              type="button"
              onClick={handleCloseModal}
              className="mr-2 bg-gray-500 hover:bg-gray-600"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {isSubmitting 
                ? (isEditing ? 'Updating...' : 'Creating...') 
                : (isEditing ? 'Update Assignment' : 'Create Assignment')
              }
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* View Assignment Details Modal */}
      <Modal 
        isOpen={isViewModalOpen} 
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedAssignment(null);
        }}
        className="max-w-3xl"
      >
        <ModalHeader>
          <h2 className="text-xl font-semibold">Assignment Details</h2>
        </ModalHeader>

        <ModalBody className="space-y-4">
          {selectedAssignment && (
            <div>
              <div className="mb-4">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {selectedAssignment.title}
                </h3>
                <div className="flex items-center space-x-2">
                  <span className={`text-xs font-medium px-2 py-1 rounded ${getAssignmentTypeColor(selectedAssignment.assignment_type)}`}>
                    {selectedAssignment.assignment_type?.toUpperCase() || 'ASSIGNMENT'}
                  </span>
                  {selectedAssignment.due_date && (
                    <span className={`text-xs font-medium px-2 py-1 rounded ${
                      new Date(selectedAssignment.due_date) < new Date() 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {new Date(selectedAssignment.due_date) < new Date() ? 'OVERDUE' : 'ACTIVE'}
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-gray-50 rounded">
                    <span className="font-medium text-gray-700">Max Score:</span>
                    <p className="text-lg font-bold text-gray-900">{selectedAssignment.max_score || 'N/A'}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded">
                    <span className="font-medium text-gray-700">Due Date:</span>
                    <p className="text-lg font-bold text-gray-900">
                      {selectedAssignment.due_date 
                        ? new Date(selectedAssignment.due_date).toLocaleDateString() 
                        : 'No due date'}
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-3">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Description:</h4>
                  <p className="text-gray-600 whitespace-pre-wrap">
                    {selectedAssignment.description || 'No description provided'}
                  </p>
                </div>

                {selectedAssignment.attachment_url && (
                  <div className="border-t border-gray-200 pt-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Attachment:</h4>
                    <Button
                      onClick={() => window.open(selectedAssignment.attachment_url, '_blank')}
                      className="bg-orange-600 hover:bg-orange-700"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download Attachment
                    </Button>
                  </div>
                )}

                <div className="border-t border-gray-200 pt-3 text-xs text-gray-500">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="font-medium">Created:</span> {new Date(selectedAssignment.created_at).toLocaleString()}
                    </div>
                    {selectedAssignment.updated_at && (
                      <div>
                        <span className="font-medium">Updated:</span> {new Date(selectedAssignment.updated_at).toLocaleString()}
                      </div>
                    )}
                    <div>
                      <span className="font-medium">Assignment ID:</span> {selectedAssignment.id}
                    </div>
                    <div>
                      <span className="font-medium">Class ID:</span> {selectedAssignment.class_id}  
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            onClick={() => {
              setIsViewModalOpen(false);
              setSelectedAssignment(null);
            }}
            className="bg-gray-500 hover:bg-gray-600"
          >
            Close
          </Button>
          {selectedAssignment && (
            <Button
              onClick={() => {
                setIsViewModalOpen(false);
                handleEditAssignment(selectedAssignment);
              }}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Edit Assignment
            </Button>
          )}
        </ModalFooter>
      </Modal>

      {/* User Submissions Modal */}
      <Modal 
        isOpen={isSubmissionsModalOpen} 
        onClose={() => {
          setIsSubmissionsModalOpen(false);
          setSelectedAssignment(null);
          setSubmissions([]);
        }}
        className="max-w-6xl"
      >
        <ModalHeader>
          <h2 className="text-xl font-semibold">
            User Submissions - {selectedAssignment?.title}
          </h2>
        </ModalHeader>

        <ModalBody>
          {loadingSubmissions ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full"></div>
              <span className="ml-3 text-gray-600">Loading submissions...</span>
            </div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-8">
              <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-500 text-lg">No submissions yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Student
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Score
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Submitted At
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {submissions.map((submission) => (
                    <tr key={submission.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {submission.user_first_name} {submission.user_last_name}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{submission.user_email}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          submission.status === 'graded' 
                            ? 'bg-green-100 text-green-800' 
                            : submission.status === 'submitted'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {submission.status?.toUpperCase() || 'PENDING'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {submission.score !== null && submission.score !== undefined 
                            ? `${submission.score} / ${selectedAssignment?.max_score || 'N/A'}`
                            : 'Not graded'
                          }
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-600">
                          {submission.submitted_at 
                            ? new Date(submission.submitted_at).toLocaleString()
                            : 'N/A'
                          }
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleGradeSubmission(submission)}
                            className="text-green-600 hover:text-green-800 font-medium"
                            title="Grade submission"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                            </svg>
                          </button>
                          {submission.submission_url && (
                            <button
                              onClick={() => window.open(submission.submission_url, '_blank')}
                              className="text-blue-600 hover:text-blue-800 font-medium"
                              title="View submission"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                          )}
                          {submission.comments && (
                            <button
                              onClick={() => alert(submission.comments)}
                              className="text-purple-600 hover:text-purple-800 font-medium"
                              title="View comments"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{submissions.length}</p>
                    <p className="text-sm text-gray-600">Total Submissions</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">
                      {submissions.filter(s => s.status === 'graded').length}
                    </p>
                    <p className="text-sm text-gray-600">Graded</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-yellow-600">
                      {submissions.filter(s => s.status !== 'graded').length}
                    </p>
                    <p className="text-sm text-gray-600">Pending</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            onClick={() => {
              setIsSubmissionsModalOpen(false);
              setSelectedAssignment(null);
              setSubmissions([]);
            }}
            className="bg-gray-500 hover:bg-gray-600"
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Grade Submission Modal */}
      <Modal 
        isOpen={isGradeModalOpen} 
        onClose={() => {
          setIsGradeModalOpen(false);
          setSelectedSubmission(null);
          setGradeFormData({
            score: '',
            feedback: '',
            graded_by: null,
          });
        }}
        className="max-w-2xl"
      >
        <form onSubmit={handleSubmitGrade}>
          <ModalHeader>
            <h2 className="text-xl font-semibold">Grade Submission</h2>
          </ModalHeader>

          <ModalBody className="space-y-4">
            {selectedSubmission && (
              <div>
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Student Information</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Name:</span>
                      <p className="text-gray-900">
                        {selectedSubmission.user_first_name} {selectedSubmission.user_last_name}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Email:</span>
                      <p className="text-gray-900">{selectedSubmission.user_email}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Assignment:</span>
                      <p className="text-gray-900">{selectedAssignment?.title}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Max Score:</span>
                      <p className="text-gray-900">{selectedAssignment?.max_score}</p>
                    </div>
                    <div className="col-span-2">
                      <span className="font-medium text-gray-700">Submitted At:</span>
                      <p className="text-gray-900">
                        {selectedSubmission.submitted_at 
                          ? new Date(selectedSubmission.submitted_at).toLocaleString()
                          : 'N/A'
                        }
                      </p>
                    </div>
                    {selectedSubmission.submission_url && (
                      <div className="col-span-2">
                        <span className="font-medium text-gray-700">Submission File:</span>
                        <button
                          type="button"
                          onClick={() => window.open(selectedSubmission.submission_url, '_blank')}
                          className="ml-2 text-blue-600 hover:text-blue-800 underline"
                        >
                          View Submission
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="score" className="block text-sm font-medium text-gray-700 mb-1">
                      Score * (Max: {selectedAssignment?.max_score})
                    </label>
                    <Input
                      id="score"
                      name="score"
                      type="number"
                      value={gradeFormData.score}
                      onChange={handleGradeFormChange}
                      required
                      min="0"
                      max={selectedAssignment?.max_score}
                      step="0.5"
                      placeholder={`Enter score (0-${selectedAssignment?.max_score})`}
                    />
                  </div>

                  <div>
                    <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-1">
                      Feedback
                    </label>
                    <textarea
                      id="feedback"
                      name="feedback"
                      value={gradeFormData.feedback}
                      onChange={handleGradeFormChange}
                      rows={5}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Enter feedback for the student (optional)"
                    />
                  </div>

                  {selectedSubmission.score !== null && selectedSubmission.score !== undefined && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-sm text-blue-800">
                        <strong>Note:</strong> This submission was previously graded with a score of{' '}
                        <strong>{selectedSubmission.score}</strong>. 
                        {selectedSubmission.feedback && ' Previous feedback will be overwritten.'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </ModalBody>

          <ModalFooter>
            <Button
              type="button"
              onClick={() => {
                setIsGradeModalOpen(false);
                setSelectedSubmission(null);
                setGradeFormData({
                  score: '',
                  feedback: '',
                  graded_by: null,
                });
              }}
              className="mr-2 bg-gray-500 hover:bg-gray-600"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isGrading}
              className="bg-green-600 hover:bg-green-700"
            >
              {isGrading ? 'Grading...' : 'Submit Grade'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
};

export default ClassAssignment;
